import { type ReactNode } from 'react';

import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import { Icon } from '#/components/Icon/Icon';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { getI18n } from '#/i18n/getI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import { LayoutHeaderAnniversaryBanner } from '#/layouts/Layout/Header/AnniversaryBanner/LayoutHeaderAnniversaryBanner';
import type { SearchFromPageType } from '#/modules/search/types/SearchFromPageType';
import type { SearchType } from '#/modules/search/types/SearchType';
import { searchGetVolopsRoutePath } from '#/modules/search/volops/routing/searchGetVolopsRoutePath';
import { homeRoute } from '#/routing/routes';
import { dynamic } from '#/utils/dynamic';

import { LayoutHeaderBottom } from './Bottom/LayoutHeaderBottom';
import { LayoutHeaderButton } from './Button/LayoutHeaderButton';
import { ApiAndIntegrationsDropdown } from './dropdowns/api-and-integrations/ApiAndIntegrationsDropdown';
import { FindAJobDropdown } from './dropdowns/find-a-job/FindAJobDropdown';
import { ParticipateDropdown } from './dropdowns/participate/ParticipateDropdown';
import { PostOnIdealistDropdown } from './dropdowns/post-on-idealist/PostOnIdealistDropdown';
import { VolunteerDropdown } from './dropdowns/volunteer/VolunteerDropdown';
import {
  LayoutHeaderApiAndIntegrationsDropdownHolder,
  LayoutHeaderAuthHolder,
  LayoutHeaderAuthLink,
  LayoutHeaderDesktopDonateLinkHolder,
  LayoutHeaderHolder,
  LayoutHeaderLeft,
  LayoutHeaderLinksHolder,
  LayoutHeaderLogoLink,
  LayoutHeaderRight,
  LayoutHeaderSignupLinkHolder,
  LayoutHeaderTopSectionHolder,
} from './LayoutHeader.styled';
import { LayoutHeaderMobileSearchMenu } from './LayoutHeaderMobileSearchMenu';

const DynamicLayoutHeaderUserItems = dynamic(
  () =>
    import('./UserItems/LayoutHeaderUserItems').then((m) => ({
      default: m.LayoutHeaderUserItems,
    })),
  { strategy: 'always' },
);

const DONATE_BUTTON_DETAILS = {
  en: {
    text: 'Donate',
    href: 'https://giving.classy.org/campaign/673245/donate',
  },
  pt: {
    text: 'Doar',
    href: '/pt/doacao',
  },
  es: {
    text: 'Haz una Donación',
    href: '/es/dona',
  },
} as const satisfies Record<I18nLocale, { text: string; href: string }>;

const IDEALIST_DAYS_PATH = {
  en: '/en/days/get-involved',
  es: '/es/dias/participa',
  pt: '/pt/days/participe',
};

type Props = {
  defaultSearchType: SearchType;
  searchFromPageType: SearchFromPageType | undefined;
  showDesktopSearch: boolean;
  isSearchLandingPage: boolean;
  showSearchRefinements: boolean;
  searchRefinements: ReactNode;
  isSearchPage: boolean;
  user: ApiUser | null;
  locale: I18nLocale;
  redirectTo: string;
  showAnniversaryBanner: boolean;
};

export function LayoutHeader({
  defaultSearchType,
  searchFromPageType,
  showDesktopSearch,
  isSearchLandingPage,
  showSearchRefinements,
  searchRefinements,
  isSearchPage,
  user,
  locale,
  redirectTo,
  showAnniversaryBanner,
}: Props) {
  const { i18n } = getI18n(locale);

  return (
    <LayoutHeaderHolder id="layout-header" data-qa-id="page-header">
      {showAnniversaryBanner && (
        <LayoutHeaderAnniversaryBanner initialShow={showAnniversaryBanner} />
      )}

      <LayoutHeaderTopSectionHolder>
        <LayoutHeaderLeft>
          <LayoutHeaderLogoLink
            type="link"
            href={homeRoute.with({ lang: locale })}
            title="Idealist"
            clickActions={{ track: { name: 'Idealist Logo Header' } }}
            qaId="header-logo-link"
          >
            <IdealistLogo withText height={{ mobile: 28, desktop: 36 }} />
          </LayoutHeaderLogoLink>

          <LayoutHeaderLinksHolder $isInSearchPage={isSearchPage}>
            {locale === I18nLocale.ES || locale === I18nLocale.PT ? (
              <ParticipateDropdown locale={locale} user={user} />
            ) : (
              <LayoutHeaderButton
                variant="grey"
                data={{
                  type: 'link',
                  href: IDEALIST_DAYS_PATH[locale],
                  clickActions: {
                    track: {
                      name: 'Idealist Days',
                      properties: { context: 'Header' },
                    },
                  },
                }}
              >
                Get Involved
              </LayoutHeaderButton>
            )}

            <FindAJobDropdown locale={locale} />

            {[I18nLocale.EN, I18nLocale.ES].includes(locale) ? (
              <VolunteerDropdown locale={locale} />
            ) : (
              <LayoutHeaderButton
                variant="grey"
                qaId="nav-volop-link"
                data={{
                  type: 'link',
                  href: searchGetVolopsRoutePath({
                    locale,
                    embedSearchWidgetId: null,
                  }),
                  clickActions: {
                    track: {
                      name: 'Volunteer',
                      properties: { context: 'Header' },
                    },
                  },
                }}
              >
                {locale === I18nLocale.PT ? 'Voluntariar' : i18n('Volunteer')}
              </LayoutHeaderButton>
            )}

            <PostOnIdealistDropdown locale={locale} user={user} />

            {locale === I18nLocale.EN && (
              <LayoutHeaderApiAndIntegrationsDropdownHolder>
                <ApiAndIntegrationsDropdown />
              </LayoutHeaderApiAndIntegrationsDropdownHolder>
            )}

            {user?.orgs.length === 0 && (
              <LayoutHeaderDesktopDonateLinkHolder>
                <LayoutHeaderButton
                  variant="grey"
                  data={{
                    type: 'link',
                    href: DONATE_BUTTON_DETAILS[locale].href,
                    clickActions: {
                      track: {
                        name: 'Donate',
                        properties: { context: 'Header' },
                      },
                    },
                  }}
                >
                  {DONATE_BUTTON_DETAILS[locale].text}
                </LayoutHeaderButton>
              </LayoutHeaderDesktopDonateLinkHolder>
            )}
          </LayoutHeaderLinksHolder>
        </LayoutHeaderLeft>

        <LayoutHeaderRight>
          {!user && (
            <LayoutHeaderAuthHolder>
              <LayoutHeaderAuthLink
                type="button"
                clickActions={{
                  track: { name: 'Log In Header' },
                  modal: {
                    type: 'LOGIN',
                    props: { redirectTo, manualLogin: true },
                  },
                }}
                qaId="login-link"
              >
                {i18n('Log In')}
              </LayoutHeaderAuthLink>

              <LayoutHeaderSignupLinkHolder>
                <LayoutHeaderAuthLink
                  type="button"
                  clickActions={{
                    track: { name: 'Signup Header' },
                    modal: { type: 'SIGNUP', props: {} },
                  }}
                  qaId="signup-link"
                >
                  {i18n('Sign Up')}
                </LayoutHeaderAuthLink>
              </LayoutHeaderSignupLinkHolder>
            </LayoutHeaderAuthHolder>
          )}

          <LayoutHeaderMobileSearchMenu
            trigger={
              <LayoutHeaderButton
                data={{ type: 'inert' }}
                ariaLabel={i18n('Search')}
                qaId="search-menu-button"
                variant="grey"
              >
                <Icon name="search" size={24} />
              </LayoutHeaderButton>
            }
            defaultSearchType={defaultSearchType}
            searchFromPageType={searchFromPageType}
            isSearchLandingPage={isSearchLandingPage}
            isSearchPage={isSearchPage}
          />

          {user && <DynamicLayoutHeaderUserItems />}
        </LayoutHeaderRight>
      </LayoutHeaderTopSectionHolder>

      <LayoutHeaderBottom
        defaultSearchType={defaultSearchType}
        searchFromPageType={searchFromPageType}
        showDesktopSearch={showDesktopSearch}
        isSearchLandingPage={isSearchLandingPage}
        showSearchRefinements={showSearchRefinements}
        searchRefinements={searchRefinements}
        isSearchPage={isSearchPage}
      />
    </LayoutHeaderHolder>
  );
}
